// El mundo de la oficina a pantalla completa (S11, brief §5 + crítica C1/C2/A5/A7). Canvas nítido (zoom
// entero) + capa DOM con lo que se lee y se pulsa. Un único sistema de señales:
//   · Etiqueta de nombre (pastilla oscura, radio 8) con el punto de estado.
//   · Si te necesita / está bloqueado / tiene un problema: la etiqueta se RELLENA de azul / ámbar / rojo y
//     sobre la cabeza aparece una insignia de 28 px (la azul late).
//   · Al pasar el ratón (o con el foco): tarjeta con el nombre, la frase completa y «Abrir chat».
// Si el mundo no cabe a zoom 2, la escena se desplaza y arranca centrada en la oficina.
import { ArrowDown, ArrowUp, Bell, Check, LayoutGrid, MessageCircle, Plus, Settings2, TriangleAlert, X, type LucideIcon } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as EventoPuntero } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { formatClockTime } from '../../../lib/time';
import { persona, retratoDe } from '../../../lib/oficina/pixel-art';
import type { ContratadoDTO, EstadoVisual, SalaDTO } from '../compartido/vocabulario';
import { COLOR_PUNTO } from '../ui';
import { hace, lineaDeEstado, quien } from '../marco/estado';
import { aspectoDe, necesitaAPau, seVa } from '../marco/situacion';
import { useSantiPorAgente } from '../pelotas/porAgente';
import { tituloPuesto } from '../plan/puesto';
import { dibujarMundo } from './dibujar';
import { generarMundo, type MesaMundo } from './generar';
import { poseDe } from './pose';
import { resumenLetrero } from './letrero';
import { MenuAgente } from './MenuAgente';
import { usePulsacionLarga } from './usePulsacionLarga';
import type { OrdenMesas } from '../api';
import { topeMesas } from '../compartido/mesas';

/**
 * 09-10 — Pau arrastra a un agente a otra mesa de su sala. Con ratón, siempre (pulsar sin mover abre el chat, como
 * antes); con el dedo, solo en «Ordenar» (fuera de ahí el dedo desplaza la oficina). `sobre` = la mesa de destino.
 */
interface Arrastre {
  agentId: string;
  projectId: string;
  desde: number;
  pointerId: number;
  x0: number;
  y0: number;
  /** Posición del puntero dentro del mundo, en píxeles de pantalla (para el fantasma). */
  px: number;
  py: number;
  activo: boolean;
  sobre: number | null;
}
const UMBRAL_ARRASTRE_PX = 6;
/** Cerca del borde de la vista, el arrastre desplaza la oficina (para llegar a una mesa que no se ve). */
const BORDE_DESPLAZA_PX = 48;

/** El agente que va colgando del puntero: su muñeco (el mismo del lienzo) y su nombre. */
function Fantasma({ c, z, x, y }: { c: ContratadoDTO; z: number; x: number; y: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;
    ctx.setTransform(z, 0, 0, z, 0, 0);
    ctx.clearRect(0, 0, 40, 36);
    persona(ctx, 20, 2, retratoDe(c.semillaAvatar || c.agentId), 'lee', 1, true);
  }, [c, z]);
  return (
    <div className="pointer-events-none absolute z-50 flex -translate-x-1/2 -translate-y-[70%] flex-col items-center drop-shadow-[0_8px_16px_rgba(0,0,0,0.6)]" style={{ left: x, top: y }} aria-hidden>
      <canvas ref={ref} width={40 * z} height={36 * z} className="[image-rendering:pixelated]" />
      <span className="-mt-1 rounded-o-s bg-oficina-acento px-2 py-0.5 text-o-nota font-semibold text-oficina-acento-tinta shadow">{quien(c)}</span>
    </div>
  );
}

const RELLENO: Partial<Record<EstadoVisual, string>> = {
  te_necesita: 'bg-oficina-te-necesita text-oficina-acento-tinta',
  bloqueado: 'bg-oficina-bloqueado text-oficina-fondo',
  error: 'bg-oficina-error text-white',
};

/**
 * Marcador opcional por contratado (S12, maqueta): si se pasa, sustituye la insignia, el relleno de la etiqueta,
 * el punto y la frase de la tarjeta. Sin él, la escena pinta exactamente lo de siempre.
 */
export interface MarcadorMundo {
  /** Le toca a quien mira: solo esto cuenta para «↓ te necesita» y para dónde arranca la cámara (nunca lo de otro). */
  destaca: boolean;
  icono: LucideIcon | null;
  relleno: string | null;
  punto: string | null;
  late: boolean;
  texto: string;
  /** 06-10 — de otra persona (nombre de pila del dueño): su etiqueta va apagada y dice de quién es. */
  de?: string;
}

export function Mundo({
  salas,
  contratados,
  panelIzqPx,
  resaltado,
  alAbrir,
  alContratar,
  alAjustarSala,
  marcador,
  alOrdenarMesas,
}: {
  salas: SalaDTO[];
  contratados: Record<string, ContratadoDTO>;
  panelIzqPx: number;
  /** Agente resaltado desde la lista (o con la tarjeta forzada en la maqueta). */
  resaltado?: string | null;
  alAbrir: (agentId: string) => void;
  alContratar: (salaId?: string) => void;
  alAjustarSala?: (salaId: string) => void;
  marcador?: (c: ContratadoDTO) => MarcadorMundo;
  /** 09-10 — ordenar las mesas a mano (mover, poner, quitar). Sin ella (maquetas) no se ordena nada. */
  alOrdenarMesas?: (projectId: string, orden: OrdenMesas) => void;
}) {
  // LA TARJETA ES UN ESTADO, NO UN :hover/:focus-within (Pau 28-09: «el cuadradito de abrir el chat se queda
  // bloqueado»): al pulsar, el foco se quedaba DENTRO y la tarjeta seguía encima del chat ya abierto. Ahora se
  // abre al pasar el ratón (con un respiro), aguanta mientras el ratón va de la mesa a la tarjeta y se cierra
  // al pulsar. Cerrada no se pinta: nada invisible queda tapando la oficina.
  const [tarjeta, setTarjeta] = useState<string | null>(null);
  const relojTarjeta = useRef<ReturnType<typeof setTimeout>>();
  const programarTarjeta = (id: string | null, ms: number) => {
    clearTimeout(relojTarjeta.current);
    relojTarjeta.current = setTimeout(() => setTarjeta(id), ms);
  };
  useEffect(() => () => clearTimeout(relojTarjeta.current), []);
  const abrir = (id: string) => {
    clearTimeout(relojTarjeta.current);
    setTarjeta(null);
    (document.activeElement as HTMLElement | null)?.blur?.();
    alAbrir(id);
  };
  const marco = useRef<HTMLDivElement>(null);
  const caja = useRef<HTMLDivElement>(null);
  const lienzo = useRef<HTMLCanvasElement>(null);
  const [hueco, setHueco] = useState({ w: 0, h: 0 });
  // SE MIDE EL MARCO, NO LA CAJA QUE SE DESPLAZA (parpadeo 28-09): el tamaño útil de la caja cambia cuando
  // le salen barras de desplazamiento (en Windows ocupan sitio), y el mundo se calcula con ese tamaño: el
  // mundo cambiaba las barras, las barras cambiaban el mundo, 60 veces por segundo. El marco no tiene barras.
  useEffect(() => {
    const el = marco.current;
    if (!el) return;
    const medir = () => setHueco({ w: el.clientWidth, h: el.clientHeight });
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    medir();
    return () => ro.disconnect();
  }, []);

  const lista = useMemo(() => Object.values(contratados), [contratados]);
  const santi = useSantiPorAgente();
  const mundo = useMemo(
    () =>
      hueco.w && hueco.h
        ? generarMundo({
            anchoPx: hueco.w,
            altoPx: hueco.h,
            panelIzqPx,
            salas: salas.map((s) => ({ projectId: s.projectId, nombre: s.nombre, color: s.color, principal: s.principal, mesas: s.mesas, huecos: s.huecos })),
            // 29-10 (Pau): los que se van no ocupan mesa; esperan en la fila de la salida, apagados.
            contratados: lista.filter((c) => !seVa(c)).map((c) => ({ agentId: c.agentId, projectId: c.projectId, mesa: c.mesa })),
            saliendo: lista.filter(seVa).map((c) => c.agentId),
          })
        : null,
    [hueco, panelIzqPx, salas, lista],
  );

  // ── 09-10: ordenar las mesas a mano ──
  // «Ordenar» se abre por sala: enseña la × de las mesas vacías, el + de los huecos y deja arrastrar con el dedo.
  const [ordenando, setOrdenando] = useState<string | null>(null);
  const salaOrdenando = salas.find((s) => s.projectId === ordenando) ?? null;
  useEffect(() => {
    if (ordenando && !salaOrdenando) setOrdenando(null); // la sala se quitó
  }, [ordenando, salaOrdenando]);
  const capa = useRef<HTMLDivElement>(null);
  const mundoRef = useRef(mundo);
  mundoRef.current = mundo;
  const [arrastre, setArrastre] = useState<Arrastre | null>(null);
  const arrastreRef = useRef<Arrastre | null>(null);
  const finArrastre = useRef<() => void>(() => undefined);
  /** El clic que el navegador manda al soltar un arrastre no abre el chat. */
  const trasArrastre = useRef(false);
  useEffect(() => () => finArrastre.current(), []);
  const mesaBajo = (clientX: number, clientY: number, a: Pick<Arrastre, 'projectId'>): { indice: number | null; px: number; py: number } => {
    const m = mundoRef.current;
    const r = capa.current?.getBoundingClientRect();
    if (!m || !r) return { indice: null, px: 0, py: 0 };
    const px = clientX - r.left;
    const py = clientY - r.top;
    const x = px / m.z;
    const y = py / m.z;
    const me = m.mesas.find((x0) => x0.projectId === a.projectId && !x0.hueco && x >= x0.rect.x && x < x0.rect.x + x0.rect.w && y >= x0.rect.y && y < x0.rect.y + x0.rect.h);
    return { indice: me ? me.indice : null, px, py };
  };
  const empezarArrastre = (e: EventoPuntero, c: ContratadoDTO, desde: number) => {
    if (!alOrdenarMesas || e.button !== 0 || arrastreRef.current) return;
    if (e.pointerType !== 'mouse' && ordenando !== c.projectId) return;
    arrastreRef.current = { agentId: c.agentId, projectId: c.projectId, desde, pointerId: e.pointerId, x0: e.clientX, y0: e.clientY, px: 0, py: 0, activo: false, sobre: null };
    let fotograma = 0;
    const cancelar = () => {
      finArrastre.current();
      cancelAnimationFrame(fotograma);
      // Un arrastre cancelado con Escape tampoco abre el chat al soltar (revisión 09-10).
      if (arrastreRef.current?.activo) {
        trasArrastre.current = true;
        setTimeout(() => (trasArrastre.current = false), 0);
      }
      arrastreRef.current = null;
      setArrastre(null);
    };
    const mover = (ev: PointerEvent) => {
      const a = arrastreRef.current;
      if (!a || ev.pointerId !== a.pointerId) return;
      if (!a.activo && Math.hypot(ev.clientX - a.x0, ev.clientY - a.y0) < UMBRAL_ARRASTRE_PX) return;
      if (!a.activo) {
        clearTimeout(relojTarjeta.current);
        setTarjeta(null);
      }
      // Cerca del borde de la vista, la oficina se desplaza (si es más grande que la ventana).
      const vista = caja.current;
      if (vista) {
        const r = vista.getBoundingClientRect();
        const dy = ev.clientY < r.top + BORDE_DESPLAZA_PX ? -24 : ev.clientY > r.bottom - BORDE_DESPLAZA_PX ? 24 : 0;
        // Encima de la lista de la izquierda no se desplaza; justo a su derecha, hacia la izquierda.
        const izq = r.left + panelIzqPx;
        const dx = ev.clientX < izq ? 0 : ev.clientX < izq + BORDE_DESPLAZA_PX ? -24 : ev.clientX > r.right - BORDE_DESPLAZA_PX ? 24 : 0;
        if (dx) vista.scrollLeft += dx;
        if (dy) vista.scrollTop += dy;
      }
      const b = mesaBajo(ev.clientX, ev.clientY, a);
      const sig = { ...a, activo: true, sobre: b.indice, px: b.px, py: b.py };
      arrastreRef.current = sig;
      // Un repintado por fotograma, no por cada movimiento del ratón (revisión 09-10).
      cancelAnimationFrame(fotograma);
      fotograma = requestAnimationFrame(() => setArrastre(arrastreRef.current));
    };
    const soltar = (ev: PointerEvent) => {
      const a = arrastreRef.current;
      if (!a || ev.pointerId !== a.pointerId) return;
      cancelar();
      if (!a.activo) return;
      if (ev.type === 'pointerup' && a.sobre !== null && a.sobre !== a.desde) alOrdenarMesas(a.projectId, { accion: 'mover', agentId: a.agentId, mesa: a.sobre });
    };
    const tecla = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') cancelar();
    };
    finArrastre.current = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
      window.removeEventListener('keydown', tecla);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
    window.addEventListener('keydown', tecla);
  };
  /** Teclado (en «Ordenar»): las flechas llevan al agente a la mesa anterior o siguiente de su sala, saltando huecos. */
  const moverConTeclado = (e: { key: string; preventDefault: () => void }, c: ContratadoDTO, desde: number) => {
    if (!alOrdenarMesas || ordenando !== c.projectId || !mundo) return;
    const paso = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!paso) return;
    e.preventDefault();
    const reales = mundo.mesas.filter((x) => x.projectId === c.projectId && !x.hueco).map((x) => x.indice);
    const destino = reales[reales.indexOf(desde) + paso];
    if (destino !== undefined) alOrdenarMesas(c.projectId, { accion: 'mover', agentId: c.agentId, mesa: destino });
  };
  // Escape cierra «Ordenar» (si no hay un arrastre en marcha: ese lo cancela él).
  useEffect(() => {
    if (!ordenando) return;
    const tecla = (ev: KeyboardEvent) => {
      // Si otra cosa encima ya usó el Escape (Ctrl+K, un diálogo), no es para «Ordenar».
      if (ev.key === 'Escape' && !ev.defaultPrevented && !arrastreRef.current && !document.querySelector('[role="dialog"]')) setOrdenando(null);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [ordenando]);
  const arrastrando = arrastre?.activo ? arrastre.agentId : null;
  // 09-10 — el menú de un agente: mantener el dedo (iPad) o clic derecho. Fuera de «Ordenar» el dedo sigue
  // desplazando la oficina (moverse cancela la pulsación) y el clic de levantar el dedo no abre el chat.
  const [menu, setMenu] = useState<string | null>(null);
  const abrirMenu = (id: string) => {
    clearTimeout(relojTarjeta.current);
    setTarjeta(null);
    setMenu(id);
  };
  const pulsacion = usePulsacionLarga(abrirMenu);
  /** Mesas de verdad (sin huecos) por sala: la última no se puede quitar. */
  const mesasReales = new Map<string, number>();
  const puestosSala = new Map<string, number>();
  for (const me of mundo?.mesas ?? []) {
    if (!me.vacio) puestosSala.set(me.projectId, (puestosSala.get(me.projectId) ?? 0) + 1);
    if (!me.hueco) mesasReales.set(me.projectId, (mesasReales.get(me.projectId) ?? 0) + 1);
  }

  // Si el mundo es más grande que la ventana: arrancar centrado en quien te necesita (si hay) o en la
  // oficina (crítica N1). La posición del desplazamiento se sigue para el indicador de borde y la tarjeta.
  const [vista, setVista] = useState({ x: 0, y: 0 });
  // «Te necesita» = lo mismo que la lista (situacion.ts necesitaAPau), y solo lo de quien mira: el marcador ya lo resuelve
  // (04-11, auditoría B4: contaba los de otra persona y el «↓ 1 te necesita» llevaba a una mesa apagada «de Carlos»).
  const leTocaA = (id: string | null | undefined) => {
    const c = id ? contratados[id] : undefined;
    return !!c && (marcador ? marcador(c).destaca : necesitaAPau(c, santi.get(c.agentId)));
  };
  const primeraQueNecesita = mundo?.mesas.find((me) => leTocaA(me.agentId));
  useLayoutEffect(() => {
    const el = caja.current;
    if (!el || !mundo) return;
    const e = mundo.encuadre;
    const objetivoY = primeraQueNecesita ? primeraQueNecesita.rect.y + primeraQueNecesita.rect.h / 2 : e.y + e.h / 2;
    el.scrollLeft = Math.max(0, (e.x + e.w / 2) * mundo.z - (panelIzqPx + (el.clientWidth - panelIzqPx) / 2));
    el.scrollTop = Math.max(0, objetivoY * mundo.z - el.clientHeight / 2);
    setVista({ x: el.scrollLeft, y: el.scrollTop });
    // Solo al cambiar la GEOMETRÍA del mundo (no cada vez que llega un aviso y se regenera igual), para no
    // mover la cámara bajo el ratón de Pau.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mundo?.z, mundo?.ancho, mundo?.alto, panelIzqPx]);
  useEffect(() => {
    const el = caja.current;
    if (!el) return;
    let raf = 0;
    const alMover = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setVista({ x: el.scrollLeft, y: el.scrollTop }));
    };
    el.addEventListener('scroll', alMover, { passive: true });
    return () => {
      el.removeEventListener('scroll', alMover);
      cancelAnimationFrame(raf);
    };
  }, []);
  // Quién te necesita y queda fuera de la vista (arriba o abajo): indicador en el borde (como Gather).
  const fuera = { arriba: [] as MesaMundo[], abajo: [] as MesaMundo[] };
  if (mundo && hueco.h) {
    for (const me of mundo.mesas) {
      if (!leTocaA(me.agentId)) continue;
      const y0 = me.rect.y * mundo.z - vista.y;
      if (y0 + me.rect.h * mundo.z * 0.5 < 0) fuera.arriba.push(me);
      else if (y0 + me.rect.h * mundo.z * 0.5 > hueco.h) fuera.abajo.push(me);
    }
  }
  const irA = (me: MesaMundo) => {
    const el = caja.current;
    if (el && mundo) el.scrollTo({ top: Math.max(0, (me.rect.y + me.rect.h / 2) * mundo.z - el.clientHeight / 2), behavior: 'smooth' });
  };

  useEffect(() => {
    const cv = lienzo.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !ctx || !mundo) return;
    ctx.imageSmoothingEnabled = false;
    ctx.setTransform(mundo.z, 0, 0, mundo.z, 0, 0);
    // 09-10 — muñeco y monitor salen de la situación (como la lista), no del estado visual viejo (pose.ts).
    const encendidas = new Set(lista.filter((c) => poseDe(c).monitor).map((c) => c.agentId));
    dibujarMundo(ctx, mundo, encendidas);
    for (const me of mundo.mesas) {
      const c = me.agentId ? contratados[me.agentId] : undefined;
      // El que va colgando del puntero no se pinta en su silla (va en el fantasma).
      if (c && c.agentId !== arrastrando) persona(ctx, me.asiento.x, me.asiento.y - 4, retratoDe(c.semillaAvatar || c.agentId), poseDe(c).animacion, 1, false);
    }
    // 29-10 — los que se van, en la fila de la salida: medio transparentes, sin monitor ni avisos.
    for (const s of mundo.salida) {
      const c = contratados[s.agentId];
      if (c) persona(ctx, s.asiento.x, s.asiento.y - 4, retratoDe(c.semillaAvatar || c.agentId), 'lee', 1, true, 0.45);
    }
    ctx.globalAlpha = 1;
    // La recepción tiene a alguien: es donde se contrata (Pau, 28-09: «está de decoración»).
    if (mundo.recepcion) persona(ctx, mundo.recepcion.x + mundo.recepcion.w / 2 - 24, mundo.recepcion.y - 14, retratoDe('recepcion'), 'lee', 1, false);
  }, [mundo, contratados, lista, arrastrando]);

  const zonaDe = (projectId: string) => salas.find((s) => s.projectId === projectId)?.nombre ?? '';
  // 09-10 — el letrero de cada sala resume lo que le toca a quien mira (letrero.ts). Los de otra persona (los que el
  // marcador apaga con «de») no cuentan: lo suyo es de su dueño, como en la lista.
  const resumenSalas = new Map(salas.map((s) => [s.projectId, resumenLetrero(lista.filter((c) => c.projectId === s.projectId && !marcador?.(c).de), santi)]));

  return (
    // `isolate`: el mundo es una capa propia. Sus tarjetas y avisos (z-40 dentro) se ordenan solo entre ellos y
    // nunca se pintan encima de la lista ni del chat que el marco pone por encima (06-10, iPad de Pau).
    <div ref={marco} className="absolute inset-0 isolate">
      <div ref={caja} className={cn('absolute inset-0 bg-oficina-fondo [scrollbar-width:thin]', mundo?.desborda ? cn('overflow-y-auto', mundo.ancho * mundo.z - hueco.w >= mundo.z ? 'overflow-x-auto' : 'overflow-x-hidden') : 'overflow-hidden')}>
        {mundo ? (
          <div ref={capa} className={cn('relative', arrastrando && 'cursor-grabbing select-none')} style={{ width: mundo.ancho * mundo.z, height: mundo.alto * mundo.z }}>
            <canvas ref={lienzo} width={mundo.ancho * mundo.z} height={mundo.alto * mundo.z} className="absolute left-0 top-0 [image-rendering:pixelated]" aria-hidden="true" />
            {mundo.zonas.map((zona) => {
              const aqui = ordenando === zona.projectId;
              const resumen = resumenSalas.get(zona.projectId);
              const urgente = resumen?.urgente ? aspectoDe(resumen.urgente.c) : null;
              const IconoUrgente = urgente?.icono;
              return (
                <div key={zona.projectId} className="absolute flex items-center gap-1.5" style={{ left: zona.letrero.x * mundo.z, top: zona.letrero.y * mundo.z }}>
                  <button
                    type="button"
                    onClick={() => alAjustarSala?.(zona.projectId)}
                    aria-label={[t('oficinaV2.sala.ajustes', { sala: zona.nombre }), resumen?.urgente?.texto, resumen?.trabajando?.texto].filter(Boolean).join(' · ')}
                    className="group flex items-center gap-1.5 rounded-o-s bg-oficina-fondo/90 px-2.5 py-1 text-o-sub font-semibold text-white shadow outline-none transition-colors hover:bg-oficina-fondo focus-visible:ring-2 focus-visible:ring-oficina-enlace"
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: zona.color }} aria-hidden />
                    {zona.nombre}
                    {resumen?.urgente && urgente && IconoUrgente ? (
                      <span className="flex items-center gap-1" data-testid="letrero-urgente">
                        <span className={cn('inline-flex h-5 w-5 items-center justify-center rounded-full', urgente.insignia)} aria-hidden>
                          <IconoUrgente className="h-3 w-3" strokeWidth={2.5} />
                        </span>
                        {resumen.urgente.n}
                      </span>
                    ) : null}
                    {resumen?.trabajando ? <span className="font-normal text-oficina-texto2">{resumen.trabajando.texto}</span> : null}
                    <Settings2 className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-80" aria-hidden />
                  </button>
                  {alOrdenarMesas ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setOrdenando(aqui ? null : zona.projectId)}
                        aria-pressed={aqui}
                        aria-label={aqui ? t('oficinaV2.mesas.listo') : t('oficinaV2.mesas.ordenarSala', { sala: zona.nombre })}
                        title={aqui ? undefined : t('oficinaV2.mesas.ordenarSala', { sala: zona.nombre })}
                        className={cn(
                          'flex items-center gap-1.5 rounded-o-s px-2 py-1 text-o-nota font-semibold shadow outline-none transition focus-visible:ring-2 focus-visible:ring-oficina-enlace',
                          aqui ? 'bg-oficina-acento text-oficina-acento-tinta hover:bg-oficina-acento-hover' : 'bg-oficina-fondo/70 text-white/70 hover:bg-oficina-fondo hover:text-white',
                        )}
                      >
                        {aqui ? <Check className="h-3.5 w-3.5" aria-hidden /> : <LayoutGrid className="h-3.5 w-3.5" aria-hidden />}
                        {aqui ? t('oficinaV2.mesas.listo') : t('oficinaV2.mesas.ordenar')}
                      </button>
                      {aqui && (puestosSala.get(zona.projectId) ?? 0) < topeMesas(salas.find((s) => s.projectId === zona.projectId)?.principal ?? false) ? (
                        <button
                          type="button"
                          onClick={() => alOrdenarMesas(zona.projectId, { accion: 'poner' })}
                          aria-label={t('oficinaV2.mesas.ponerAlFinalLargo', { sala: zona.nombre })}
                          title={t('oficinaV2.mesas.ponerAlFinalLargo', { sala: zona.nombre })}
                          className="flex items-center gap-1 rounded-o-s bg-oficina-fondo/90 px-2 py-1 text-o-nota font-semibold text-white shadow ring-1 ring-oficina-acento/50 outline-none transition-colors hover:bg-oficina-acento hover:text-oficina-acento-tinta focus-visible:ring-2 focus-visible:ring-oficina-enlace"
                        >
                          <Plus className="h-3.5 w-3.5" aria-hidden />
                          {t('oficinaV2.mesas.ponerAlFinal')}
                        </button>
                      ) : null}
                    </>
                  ) : null}
                </div>
              );
            })}
            {/* 29-10 — los que se van: un rótulo apagado y, sobre cada uno, un botón invisible para abrir su chat. */}
            {mundo.salida.length ? (
              <span
                className="pointer-events-none absolute -translate-x-full -translate-y-1/2 whitespace-nowrap text-o-mini font-semibold text-white/40"
                // A la izquierda de la fila, a su altura: en el suelo junto a la salida, nunca encima de una sala.
                style={{ left: (Math.min(...mundo.salida.map((x) => x.asiento.x)) - 18) * mundo.z, top: (mundo.salida[0]!.asiento.y - 12) * mundo.z }}
                data-testid="rotulo-se-van"
              >
                {t('oficinaV2.s12.grupo.se_van')}
              </span>
            ) : null}
            {mundo.salida.map((s) => {
              const c = contratados[s.agentId];
              if (!c) return null;
              return (
                <button
                  key={`sale-${s.agentId}`}
                  type="button"
                  onClick={() => abrir(c.agentId)}
                  aria-label={`${quien(c)} · ${t('oficinaV2.s12.situacion.saliendo')}`}
                  title={quien(c)}
                  className="absolute rounded-o-s outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace"
                  style={{ left: (s.asiento.x - 14) * mundo.z, top: (s.asiento.y - 30) * mundo.z, width: 28 * mundo.z, height: 34 * mundo.z }}
                  data-testid="sale"
                />
              );
            })}
            {mundo.mesas.map((me) => {
              const estilo = { left: me.rect.x * mundo.z, top: me.rect.y * mundo.z, width: me.rect.w * mundo.z, height: me.rect.h * mundo.z };
              const c = me.agentId ? contratados[me.agentId] : undefined;
              const ordenandoAqui = ordenando === me.projectId;
              // Destino del arrastre: la mesa bajo el puntero se marca (si hay alguien, se cambian el sitio).
              // `dentro`: va dentro de la caja de la mesa (ya colocada), así que la ocupa entera; suelto, se coloca él.
              const marcaDestino = (dentro: boolean) =>
                arrastre?.activo && arrastre.projectId === me.projectId && arrastre.sobre === me.indice && arrastre.desde !== me.indice ? (
                  <span
                    key={`destino-${me.projectId}#${me.indice}`}
                    className={cn('pointer-events-none absolute z-10 flex items-end justify-center rounded-o-m border-2 border-dashed border-oficina-acento bg-oficina-acento/15 pb-1', dentro && 'inset-0')}
                    style={dentro ? undefined : estilo}
                    aria-hidden
                  >
                    <span className="rounded-md bg-oficina-acento px-1.5 text-o-mini font-semibold text-oficina-acento-tinta">{t(c ? 'oficinaV2.mesas.cambiarSitio' : 'oficinaV2.mesas.moverAqui')}</span>
                  </span>
                ) : null;
              if (me.hueco) {
                // Pau, 09-10: «hacer clic y añadir una mesa en el espacio que ha quedado hueco», sin abrir «Ordenar».
                // Fuera de «Ordenar», el sitio se insinúa (contorno tenue) y se marca del todo (con su +) al pasar el ratón.
                if (!alOrdenarMesas) return null;
                return (
                  <button
                    key={`${me.projectId}#${me.indice}`}
                    type="button"
                    onClick={() => alOrdenarMesas(me.projectId, { accion: 'poner', mesa: me.indice })}
                    aria-label={t('oficinaV2.mesas.ponerAqui', { n: me.indice + 1, sala: zonaDe(me.projectId) })}
                    title={t('oficinaV2.mesas.ponerAqui', { n: me.indice + 1, sala: zonaDe(me.projectId) })}
                    className={cn(
                      'group absolute flex items-center justify-center rounded-o-m border border-dashed outline-none transition-colors hover:border-oficina-acento hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-oficina-enlace',
                      ordenandoAqui ? 'border-white/25' : 'border-white/[0.08]',
                    )}
                    style={estilo}
                  >
                    <span
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full bg-oficina-fondo/80 text-white/70 ring-1 ring-white/30 transition group-hover:text-white group-hover:opacity-100 group-focus-visible:opacity-100',
                        // Fuera de «Ordenar», solo el contorno: el + sale al pasar el ratón (no se confunde con el + de
                        // contratar de la mesa libre). En «Ordenar», siempre.
                        !ordenandoAqui && 'opacity-0',
                      )}
                    >
                      <Plus className="h-4 w-4" aria-hidden />
                    </span>
                  </button>
                );
              }
              if (!c && ordenandoAqui) {
                const ultima = (mesasReales.get(me.projectId) ?? 0) <= 1;
                return (
                  <div key={`${me.projectId}#${me.indice}`} className="absolute flex items-center justify-center rounded-o-m border border-dashed border-white/20" style={estilo}>
                    {/* En «Ordenar», la primera libre enseña su + de contratar (fuera, con ratón, solo al pasar por encima). */}
                    {me.libre ? (
                      <button
                        type="button"
                        onClick={() => alContratar(me.projectId)}
                        aria-label={t('oficinaV2.escena.mesaLibre', { sala: zonaDe(me.projectId) })}
                        title={t('oficinaV2.escena.mesaLibre', { sala: zonaDe(me.projectId) })}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-oficina-fondo/80 text-white shadow ring-1 ring-white/40 outline-none transition-colors hover:bg-oficina-acento hover:text-oficina-acento-tinta focus-visible:ring-2 focus-visible:ring-oficina-enlace"
                      >
                        <Plus className="h-5 w-5" aria-hidden />
                      </button>
                    ) : null}
                    {marcaDestino(true)}
                    {ultima ? null : (
                      <button
                        type="button"
                        onClick={() => alOrdenarMesas?.(me.projectId, { accion: 'quitar', mesa: me.indice })}
                        aria-label={t('oficinaV2.mesas.quitar', { n: me.indice + 1, sala: zonaDe(me.projectId) })}
                        title={t('oficinaV2.mesas.quitar', { n: me.indice + 1, sala: zonaDe(me.projectId) })}
                        className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-oficina-fondo/90 text-white/80 shadow ring-1 ring-white/30 outline-none transition-colors hover:bg-oficina-error hover:text-white focus-visible:ring-2 focus-visible:ring-oficina-enlace"
                      >
                        <X className="h-4 w-4" aria-hidden />
                      </button>
                    )}
                  </div>
                );
              }
              if (!c) {
                if (!me.libre) return marcaDestino(false);
                return (
                  <button
                    key={`${me.projectId}#${me.indice}`}
                    type="button"
                    onClick={() => alContratar(me.projectId)}
                    aria-label={t('oficinaV2.escena.mesaLibre', { sala: zonaDe(me.projectId) })}
                    title={t('oficinaV2.escena.mesaLibre', { sala: zonaDe(me.projectId) })}
                    className="group absolute flex items-center justify-center rounded-o-m outline-none transition-colors hover:bg-white/10 focus-visible:bg-white/10"
                    style={estilo}
                  >
                    {/* 09-10 (Pau, «menos +»): con el dedo, a la vista (solo esta, la primera libre de la sala: en el iPad
                        no hay «pasar el ratón»); con ratón, sale al pasar por la mesa o en «Ordenar». */}
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-oficina-fondo/80 text-white shadow ring-1 ring-white/40 transition [@media(hover:hover)_and_(pointer:fine)]:opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100">
                      <Plus className="h-5 w-5" aria-hidden />
                    </span>
                    {marcaDestino(true)}
                  </button>
                );
              }
              const m = marcador?.(c);
              const estado = m ? { texto: m.texto, tono: 'normal' as const } : lineaDeEstado(c);
              const relleno = m ? m.relleno : RELLENO[c.visual];
              const IconoMarca = m ? m.icono : c.visual === 'te_necesita' ? Bell : TriangleAlert;
              const late = m ? m.late : c.visual === 'te_necesita';
              const punto = m ? m.punto : COLOR_PUNTO[c.visual];
              const zt = mundo.z;
              // El sprite se dibuja con su borde superior (el pelo) en asiento.y − 4: ahí empieza la cabeza.
              const cabeza = (me.asiento.y - 4 - me.rect.y) * zt;
              const pie = (me.asiento.y - me.rect.y + 24) * zt;
              return (
                <div
                  key={c.agentId}
                  // Sin menú del sistema ni selección de texto al mantener el dedo: la pulsación larga es nuestra.
                  // 04-11 — en «Ordenar», cada mesa ocupada se marca con un contorno discontinuo (se puede coger).
                  className={cn('absolute select-none [-webkit-touch-callout:none]', ordenandoAqui && 'touch-none cursor-grab rounded-o-m outline-dashed outline-1 outline-white/25', arrastrando === c.agentId && 'opacity-40')}
                  style={estilo}
                  onPointerDown={(e) => {
                    // Lo que va en un portal (el menú del agente) burbujea por React hasta aquí aunque no esté dentro de
                    // la mesa: pulsar en una opción y mover el ratón movía al agente de mesa (04-11, auditoría B5). Solo
                    // cuenta lo que de verdad está dentro de la mesa.
                    if (!e.currentTarget.contains(e.target as Node)) return;
                    pulsacion.empezar(e, c.agentId, !ordenandoAqui);
                    empezarArrastre(e, c, me.indice);
                  }}
                  onContextMenu={(e) => {
                    if (ordenandoAqui || !e.currentTarget.contains(e.target as Node)) return;
                    e.preventDefault();
                    abrirMenu(c.agentId);
                  }}
                  onPointerEnter={(e) => e.pointerType === 'mouse' && !arrastreRef.current && programarTarjeta(c.agentId, 150)}
                  onPointerLeave={(e) => e.pointerType === 'mouse' && programarTarjeta(null, 250)}
                >
                  {marcaDestino(true)}
                  <button
                    type="button"
                    onClick={() => {
                      // El clic de levantar el dedo tras abrir el menú, o de soltar un arrastre, no abre el chat.
                      if (pulsacion.consumir() || trasArrastre.current) return;
                      abrir(c.agentId);
                    }}
                    onKeyDown={(e) => moverConTeclado(e, c, me.indice)}
                    onFocus={(e) => e.currentTarget.matches(':focus-visible') && setTarjeta(c.agentId)}
                    onBlur={() => programarTarjeta(null, 150)}
                    aria-label={t('oficinaV2.escena.personaje', { nombre: quien(c), puesto: tituloPuesto(c.puesto), sala: zonaDe(c.projectId), estado: estado.texto })}
                    className={cn('absolute inset-0 rounded-o-m outline-none transition-colors hover:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-oficina-enlace', resaltado === c.agentId && 'bg-white/[0.08] ring-2 ring-white/70')}
                  />
                  {relleno && IconoMarca ? (
                    <span
                      className={cn('pointer-events-none absolute left-1/2 flex h-7 w-7 -translate-x-1/2 -translate-y-full items-center justify-center rounded-full shadow-o-flota ring-2 ring-black/25', relleno, late && 'motion-safe:animate-[latido_1.6s_ease-in-out_infinite]')}
                      style={{ top: cabeza - 8 }}
                      aria-hidden
                    >
                      <IconoMarca className="h-4 w-4" />
                    </span>
                  ) : null}
                  <span
                    className={cn(
                      // S13: hasta 2 líneas (Pau, 06-10: «Analizar rendi…» no dice nada); el punto, en la primera.
                      'pointer-events-none absolute left-1/2 flex w-max max-w-[calc(100%-6px)] -translate-x-1/2 items-start gap-1.5 rounded-o-s px-2 py-0.5 text-o-nota font-semibold leading-[15px] shadow',
                      relleno ?? 'bg-oficina-fondo/90 text-white',
                      m?.de && 'opacity-60',
                      // 04-11 — «Ordenar» como en el iPhone: las etiquetas tiemblan (desfasadas una sí, una no).
                      ordenandoAqui && 'motion-safe:animate-temblor',
                      ordenandoAqui && me.indice % 2 === 1 && '[animation-direction:reverse]',
                    )}
                    data-temblor={ordenandoAqui || undefined}
                    style={{ top: pie }}
                  >
                    {relleno || !punto ? null : <span className={cn('mt-[4px] h-2 w-2 shrink-0 rounded-full', punto)} aria-hidden />}
                    {/* De otra persona: solo de quién es (el encargo, en su tarjeta); el escritorio no da para los dos. */}
                    <span className="line-clamp-2 break-words text-center">{m?.de ? t('oficinaV2.s12.bandejas.de', { nombre: m.de }) : quien(c)}</span>
                  </span>
                  {menu === c.agentId ? (
                    <MenuAgente
                      c={c}
                      alCerrar={() => setMenu(null)}
                      alAbrir={() => abrir(c.agentId)}
                      alMover={alOrdenarMesas ? () => setOrdenando(c.projectId) : undefined}
                    />
                  ) : null}
                  {/* Tarjeta: al pasar el ratón o con el foco del teclado (y la resaltada, en la maqueta). */}
                  {tarjeta === c.agentId || resaltado === c.agentId ? (
                  <div
                    // Pulsar en la tarjeta («Abrir chat») nunca empieza un arrastre del agente.
                    onPointerDown={(e) => e.stopPropagation()}
                    className="absolute left-1/2 z-40 w-[280px] -translate-x-1/2 rounded-o-m bg-oficina-elevada/95 p-3 text-left shadow-o-hoja ring-1 ring-white/10 motion-safe:animate-[aparecer_120ms_ease-out]"
                    // Crítica N3: arriba si cabe en la vista; si no, debajo de la etiqueta.
                    style={
                      me.rect.y * zt + cabeza - vista.y > 150
                        ? { top: cabeza - (relleno ? 44 : 10), transform: 'translate(-50%, -100%)' }
                        : { top: pie + 32, transform: 'translate(-50%, 0)' }
                    }
                  >
                    <p className="flex items-baseline justify-between gap-2">
                      <span className="text-o-cuerpo font-semibold text-oficina-texto">{quien(c)}</span>
                      <span className="text-o-nota text-oficina-texto3">{m ? hace(c.ultimoInformeEn ?? c.creadoEn) : formatClockTime(c.ultimoInformeEn ?? c.creadoEn)}</span>
                    </p>
                    <p className={cn('mt-0.5 text-o-sub leading-snug', estado.tono === 'acento' ? 'text-oficina-te-necesita-claro' : 'text-oficina-texto2')}>{estado.texto}</p>
                    <button
                      type="button"
                      onClick={() => !trasArrastre.current && abrir(c.agentId)}
                      onFocus={() => clearTimeout(relojTarjeta.current)}
                      // 09-10 (Pau, «el dorado solo para lo que pide que actúes»): abrir un chat no es actuar → botón secundario.
                      className="mt-2.5 flex h-9 w-full items-center justify-center gap-2 rounded-o-s border border-white/15 text-o-texto font-semibold text-white outline-none transition-colors hover:bg-white/[0.08] focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
                    >
                      <MessageCircle className="h-4 w-4" aria-hidden />
                      {t('oficinaV2.escena.abrirChat')}
                    </button>
                  </div>
                  ) : null}
                </div>
              );
            })}
            {arrastre?.activo && contratados[arrastre.agentId] ? <Fantasma c={contratados[arrastre.agentId]!} z={mundo.z} x={arrastre.px} y={arrastre.py} /> : null}
            {mundo.recepcion ? (
              // La recepción es donde se contrata: pulsarla abre «Contratar».
              <button
                type="button"
                onClick={() => alContratar()}
                aria-label={t('oficinaV2.escena.recepcionContratar')}
                className="group absolute rounded-o-m outline-none transition-colors hover:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-oficina-enlace"
                style={{ left: mundo.recepcion.x * mundo.z, top: (mundo.recepcion.y - 20) * mundo.z, width: mundo.recepcion.w * mundo.z, height: (mundo.recepcion.h + 20) * mundo.z }}
              >
                <span className="absolute left-2 top-0 flex -translate-y-1/2 items-center gap-1.5 rounded-o-s bg-oficina-fondo/90 px-2.5 py-1 text-o-sub font-semibold text-white shadow ring-1 ring-oficina-acento/40 transition-colors group-hover:bg-oficina-acento group-hover:text-oficina-acento-tinta">
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  {/* 09-10 — el letrero dice solo «Recepción» (el + ya invita a contratar; el lector de pantalla lo oye entero). */}
                  {t('oficinaV2.escena.recepcion')}
                </span>
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {salaOrdenando && alOrdenarMesas ? (
        <div
          role="status"
          className="absolute z-40 flex max-w-[min(760px,calc(100vw-32px))] -translate-x-1/2 items-center gap-3 rounded-o-m bg-oficina-elevada/95 px-4 py-2.5 text-o-sub text-oficina-texto2 shadow-o-hoja ring-1 ring-white/10"
          style={{ left: panelIzqPx + (hueco.w - panelIzqPx) / 2, bottom: 16 }}
        >
          <span className="leading-snug">{t('oficinaV2.mesas.aviso', { sala: salaOrdenando.nombre })}</span>
          {salaOrdenando.mesas !== null || salaOrdenando.huecos.length ? (
            <button
              type="button"
              onClick={() => alOrdenarMesas(salaOrdenando.projectId, { accion: 'automatico' })}
              className="shrink-0 rounded-o-s px-2.5 py-1.5 text-o-sub font-semibold text-oficina-texto outline-none ring-1 ring-white/15 transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-oficina-enlace"
            >
              {t('oficinaV2.mesas.automatico')}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setOrdenando(null)}
            className="flex shrink-0 items-center gap-1.5 rounded-o-s bg-oficina-acento px-3 py-1.5 text-o-sub font-semibold text-oficina-acento-tinta outline-none transition-colors hover:bg-oficina-acento-hover focus-visible:ring-2 focus-visible:ring-oficina-enlace"
          >
            <Check className="h-4 w-4" aria-hidden />
            {t('oficinaV2.mesas.listo')}
          </button>
        </div>
      ) : null}
      {(['arriba', 'abajo'] as const).map((lado) =>
        fuera[lado].length ? (
          <button
            key={lado}
            type="button"
            onClick={() => irA(fuera[lado][0]!)}
            className="absolute z-40 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-oficina-acento px-3.5 py-1.5 text-o-texto font-semibold text-oficina-acento-tinta shadow-o-hoja transition-colors hover:bg-oficina-acento-hover"
            style={{ left: panelIzqPx + (hueco.w - panelIzqPx) / 2, [lado === 'arriba' ? 'top' : 'bottom']: lado === 'abajo' && salaOrdenando ? 84 : 16 }}
          >
            {lado === 'arriba' ? <ArrowUp className="h-4 w-4" aria-hidden /> : <ArrowDown className="h-4 w-4" aria-hidden />}
            {t(fuera[lado].length === 1 ? 'oficinaV2.resumen.teNecesitaUno' : 'oficinaV2.resumen.teNecesitanVarios', { n: fuera[lado].length })}
          </button>
        ) : null,
      )}
    </div>
  );
}
